import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';

import { Label } from '@eliadmin/core/src/components/ui/label';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;
  const {
    descriptionId,
    messageId,
    hasDescription,
    hasMessage,
    hasMessageContent,
  } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: descriptionId ?? `${id}-form-item-description`,
    formMessageId: messageId ?? `${id}-form-item-message`,
    hasDescription,
    hasMessage,
    hasMessageContent,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
  required?: boolean;
  descriptionId?: string;
  messageId?: string;
  hasDescription?: boolean;
  hasMessage?: boolean;
  hasMessageContent?: boolean;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

function findFormSlot(
  children: React.ReactNode,
  type: React.ElementType,
): React.ReactElement<{ id?: string; children?: React.ReactNode }> | undefined {
  for (const child of React.Children.toArray(children)) {
    if (!React.isValidElement<{ id?: string; children?: React.ReactNode }>(child)) continue;
    if (child.type === type) return child;
    if (child.type === FormItem || child.type === FormField) continue;
    if (typeof child.type === 'string' || child.type === React.Fragment) {
      const slot = findFormSlot(child.props.children, type);
      if (slot) return slot;
    }
  }
}

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { required?: boolean }
>(({ className, required = false, ...props }, ref) => {
  const id = React.useId();
  const description = findFormSlot(props.children, FormDescription);
  const message = findFormSlot(props.children, FormMessage);

  return (
    <FormItemContext.Provider
      value={{
        id,
        required,
        descriptionId: description?.props.id,
        messageId: message?.props.id,
        hasDescription: !!description,
        hasMessage: !!message,
        hasMessageContent: !!message?.props.children,
      }}
    >
      <div ref={ref} className={cn('space-y-0', className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & { isRequired?: boolean }
>(({ className, isRequired, ...props }, ref) => {
  const { error, formItemId } = useFormField();
  const { required } = React.useContext(FormItemContext);

  return (
    <Label
      ref={ref}
      className={cn(
        'text-xs font-bold text-muted-foreground sm:text-sm',
        error && 'text-destructive',
        className,
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ className, ...props }, ref) => {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId,
    hasDescription,
    hasMessage,
    hasMessageContent,
  } = useFormField();
  const describedBy =
    [
      hasDescription && formDescriptionId,
      hasMessage && (error?.message || hasMessageContent) && formMessageId,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={describedBy}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn('text-[0.8rem] text-muted-foreground', className)}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const { t } = useTranslations();
  const body = error?.message ? t(String(error?.message)) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn('text-[0.8rem] font-medium text-destructive', className)}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = 'FormMessage';

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
};
